import { type V2, angleTo, dist, dirFromAngle, normalize, sub } from '../../core/math';
import type { Actor } from '../actor';
import { registry } from '../registry';
import { applyStatus } from '../statusSystem';
import { registerBrain, type World } from '../world';

/**
 * Monster brains. Brains run only on the peer that owns the monster (the
 * instance host), decide movement and casts, and never touch rendering.
 */

interface BrainState {
  state: 'idle' | 'chase' | 'attack' | 'return' | 'wander';
  t: number;
  wanderTarget: V2 | null;
  repath: number;
  lastSkill: string;
  /** Skill cooldowns tracked by the brain in addition to skill cooldowns. */
  gcd: number;
}

function st(a: Actor): BrainState {
  const m = a.monster!;
  if (!m.brain.state) {
    Object.assign(m.brain, { state: 'idle', t: 0, wanderTarget: null, repath: 0, lastSkill: '', gcd: 0.5 + Math.random() } satisfies BrainState);
  }
  return m.brain as unknown as BrainState;
}

/** Picks the target with the most threat, preferring nearby players. */
function pickTarget(world: World, a: Actor): Actor | null {
  const m = a.monster!;
  let best: Actor | null = null;
  let bestScore = -Infinity;
  for (const [id, threat] of m.threat) {
    const t = world.actors.get(id);
    if (!t || !t.alive || !world.isEnemy(a, t)) {
      m.threat.delete(id);
      continue;
    }
    const d = dist(a.pos, t.pos);
    if (d > m.leash * 1.5) continue;
    const score = threat - d * 20;
    if (score > bestScore) {
      bestScore = score;
      best = t;
    }
  }
  if (best) return best;
  return world.nearestEnemy(a, m.aggroRange);
}

function moveToward(world: World, a: Actor, target: V2, stopAt: number, dt: number): void {
  const s = st(a);
  const d = dist(a.pos, target);
  if (d <= stopAt) {
    a.path = [];
    a.moveGoal = null;
    return;
  }
  s.repath -= dt;
  if (s.repath <= 0 || a.path.length === 0) {
    s.repath = 0.4 + Math.random() * 0.2;
    // Stop short of the target so melee monsters surround instead of stacking.
    const dir = normalize(sub(a.pos, target));
    const goal = { x: target.x + dir.x * stopAt * 0.8, z: target.z + dir.z * stopAt * 0.8 };
    a.moveGoal = goal;
    a.path = world.nav.lineOfSight(a.pos, goal) ? [goal] : (world.nav.findPath(a.pos, goal, 4000) ?? [goal]);
  }
}

/** Chooses one of the monster's skills that is ready and fits the range. */
function chooseSkill(world: World, a: Actor, target: Actor): string | null {
  const def = registry.monster(a.monster!.defId);
  const d = dist(a.pos, target.pos) - target.radius;
  const ready = def.skills.filter((s) => {
    if ((a.cooldowns.get(s.id) ?? 0) > 0) return false;
    const range = (s.tags?.find((t) => t.startsWith('range:'))?.slice(6) ?? '2.5').split('-');
    const lo = range.length > 1 ? Number(range[0]) : 0;
    const hi = Number(range[range.length - 1]);
    return d >= lo && d <= hi;
  });
  if (ready.length === 0) return null;
  void world;
  return (ready[Math.floor(Math.random() * ready.length)] as { id: string }).id;
}

function leashCheck(world: World, a: Actor): boolean {
  const m = a.monster!;
  const s = st(a);
  if (dist(a.pos, m.home) > m.leash) {
    s.state = 'return';
    m.threat.clear();
    m.targetId = null;
    a.moveGoal = { ...m.home };
    a.path = world.nav.findPath(a.pos, m.home) ?? [m.home];
    return true;
  }
  return false;
}

/** Standard melee monster: wander → aggro → chase → attack → leash. */
function meleeBrain(world: World, a: Actor, dt: number, preferRange = 0): void {
  const m = a.monster!;
  const s = st(a);
  s.t += dt;
  s.gcd -= dt;
  if (a.cast) return;
  if (s.state === 'return') {
    if (dist(a.pos, m.home) < 1.5 || a.path.length === 0) {
      s.state = 'idle';
      a.hp = a.stats.maxHp;
    }
    return;
  }
  const target = pickTarget(world, a);
  if (!target) {
    m.targetId = null;
    // Idle wander around home.
    if (s.state !== 'wander' && s.state !== 'idle') s.state = 'idle';
    if (s.t > 3 + Math.random() * 4) {
      s.t = 0;
      const ang = Math.random() * Math.PI * 2;
      const r = Math.random() * 4;
      const p = { x: m.home.x + Math.sin(ang) * r, z: m.home.z + Math.cos(ang) * r };
      if (world.nav.walkable(p)) {
        a.moveGoal = p;
        a.path = [p];
      }
    }
    return;
  }
  if (leashCheck(world, a)) return;
  m.targetId = target.id;
  if (!m.threat.has(target.id)) m.threat.set(target.id, 1);
  const d = dist(a.pos, target.pos);
  const skill = s.gcd <= 0 ? chooseSkill(world, a, target) : null;
  if (skill) {
    a.path = [];
    world.faceTowards(a, target.pos);
    if (world.monsterCast(a, skill, { ...target.pos })) {
      s.lastSkill = skill;
      s.gcd = 0.6 + Math.random() * 0.9;
    }
    return;
  }
  const want = preferRange > 0 ? preferRange : a.radius + target.radius + 0.6;
  if (preferRange > 0 && d < preferRange * 0.6) {
    // Kite away from targets that get too close.
    const away = normalize(sub(a.pos, target.pos));
    const p = { x: a.pos.x + away.x * 3, z: a.pos.z + away.z * 3 };
    if (world.nav.walkable(p)) {
      a.path = [p];
      a.moveGoal = p;
    }
    return;
  }
  moveToward(world, a, target.pos, want, dt);
  if (d < want + 0.5) world.faceTowards(a, target.pos);
}

registerBrain('melee', (w, a, dt) => meleeBrain(w, a, dt, 0));
registerBrain('ranged', (w, a, dt) => meleeBrain(w, a, dt, 9));
registerBrain('caster', (w, a, dt) => meleeBrain(w, a, dt, 11));
/** Horde swarmers: always aggressive, huge aggro range (rift dungeons). */
registerBrain('swarm', (w, a, dt) => {
  if (a.monster) a.monster.aggroRange = 60;
  meleeBrain(w, a, dt, 0);
});
registerBrain('none', () => {});
/** Stationary objects (rift cores, totems). */
registerBrain('static', () => {});

// ---------------------------------------------------------------------------
// Boss brain: weighted patterns + scripted mechanics at HP bar thresholds.

export interface BossPattern {
  skill: string;
  weight: number;
  /** Allowed distance band to the target. */
  range?: [number, number];
  /** Only in these phases (1-based). */
  phases?: number[];
  /** Target selection: current threat target, farthest player, random. */
  target?: 'threat' | 'far' | 'random';
}

export interface BossMechanic {
  /** Triggers when the boss's remaining HP bars drop to this count (or HP% if `pct`). */
  atBars?: number;
  atPct?: number;
  name: string;
  run: (world: World, boss: Actor) => void;
}

export interface BossScript {
  patterns: BossPattern[];
  /** HP% thresholds starting phases 2, 3, ... */
  phaseAt: number[];
  mechanics: BossMechanic[];
  /** Seconds until enrage (0 = none). */
  enrageAfter: number;
  onPhase?: (world: World, boss: Actor, phase: number) => void;
  /** Called every tick (for custom continuous behaviour). */
  tick?: (world: World, boss: Actor, dt: number) => void;
}

const bossScripts = new Map<string, BossScript>();
export function registerBossScript(defId: string, script: BossScript): void {
  bossScripts.set(defId, script);
}
export function getBossScript(defId: string): BossScript | undefined {
  return bossScripts.get(defId);
}

interface BossState {
  phase: number;
  fired: string[];
  gcd: number;
  enraged: boolean;
  repath: number;
  lastPattern: string;
}

export function bossBars(a: Actor): number {
  const bars = a.monster?.hpBars ?? 1;
  return Math.ceil(a.hpPct * bars);
}

registerBrain('boss', (world, a, dt) => {
  const m = a.monster!;
  const script = bossScripts.get(m.defId);
  const b = m.brain as unknown as BossState;
  if (b.phase === undefined) Object.assign(m.brain, { phase: 1, fired: [], gcd: 2, enraged: false, repath: 0, lastPattern: '' } satisfies BossState);
  b.gcd -= dt;
  script?.tick?.(world, a, dt);

  // Phase changes by HP%.
  if (script) {
    const pct = a.hpPct * 100;
    let phase = 1;
    script.phaseAt.forEach((th, i) => {
      if (pct <= th) phase = i + 2;
    });
    if (phase !== b.phase) {
      b.phase = phase;
      world.emit({ type: 'bossPhase', id: a.id, phase });
      script.onPhase?.(world, a, phase);
    }
    // Scripted mechanics fire once.
    const bars = bossBars(a);
    for (const mech of script.mechanics) {
      if (b.fired.includes(mech.name)) continue;
      const hit = (mech.atBars !== undefined && bars <= mech.atBars) || (mech.atPct !== undefined && pct <= mech.atPct);
      if (hit && !a.cast) {
        b.fired.push(mech.name);
        mech.run(world, a);
        b.gcd = Math.max(b.gcd, 1.2);
        return;
      }
    }
    if (!b.enraged && script.enrageAfter > 0 && m.age >= script.enrageAfter) {
      b.enraged = true;
      applyStatus(world, a, { id: 'enraged', duration: 1e9 }, a.id, 0);
      world.emit({ type: 'announce', text: `${a.name} is enraged!`, style: 'warning' });
    }
  }
  if (a.cast) return;

  const target = pickTarget(world, a);
  if (!target) {
    a.path = [];
    return;
  }
  m.targetId = target.id;
  if (!m.threat.has(target.id)) m.threat.set(target.id, 1);
  const d = dist(a.pos, target.pos) - target.radius - a.radius;

  if (script && b.gcd <= 0) {
    const candidates = script.patterns.filter((p) => {
      if (p.phases && !p.phases.includes(b.phase)) return false;
      if ((a.cooldowns.get(p.skill) ?? 0) > 0) return false;
      if (p.range && (d < p.range[0] || d > p.range[1])) return false;
      return true;
    });
    if (candidates.length > 0) {
      let total = 0;
      for (const c of candidates) total += c.weight * (c.skill === b.lastPattern ? 0.35 : 1);
      let roll = world.rng.next() * total;
      let pick = candidates[0] as BossPattern;
      for (const c of candidates) {
        roll -= c.weight * (c.skill === b.lastPattern ? 0.35 : 1);
        if (roll <= 0) {
          pick = c;
          break;
        }
      }
      let tgt = target;
      if (pick.target === 'far' || pick.target === 'random') {
        const players = world.playersInRange(a.pos, 60).filter((p) => world.isEnemy(a, p));
        if (players.length > 0) {
          if (pick.target === 'random') tgt = world.rng.pick(players);
          else tgt = players.reduce((x, y) => (dist(a.pos, y.pos) > dist(a.pos, x.pos) ? y : x));
        }
      }
      world.faceTowards(a, tgt.pos);
      if (world.monsterCast(a, pick.skill, { ...tgt.pos })) {
        b.lastPattern = pick.skill;
        b.gcd = 0.4 + world.rng.next() * 0.6;
        a.path = [];
        return;
      }
    }
  }
  // Close the distance.
  if (d > 1.2) {
    b.repath -= dt;
    if (b.repath <= 0 || a.path.length === 0) {
      b.repath = 0.5;
      const dir = dirFromAngle(angleTo(target.pos, a.pos));
      const goal = { x: target.pos.x + dir.x * (a.radius + 1), z: target.pos.z + dir.z * (a.radius + 1) };
      a.path = [goal];
      a.moveGoal = goal;
    }
  } else {
    a.path = [];
    world.faceTowards(a, target.pos);
  }
});
